/**
 * CARI API Client — SSE streaming + REST endpoints.
 *
 * Mirrors the shape of TERRA's API client so the frontend pattern lifts
 * cleanly. CARI's REST surface lives under /api/v1/phreatic/cari/*; the
 * SSE message stream uses the same event protocol as TERRA so the parser
 * is essentially identical.
 *
 * Differences vs TERRA's client:
 *   - Different REST prefix (/phreatic/cari instead of /terra)
 *   - No UI-action types (CARI doesn't currently emit data-vault nav
 *     actions; if it ever does, these types will get added)
 *   - Capability profile carried on each conversation record
 */
import { apiClient } from '@/api/client';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const CARI_PREFIX = '/phreatic/cari';

// ── Types ───────────────────────────────────────────────────────────────

export type CapabilityProfile =
  | 'dev'
  | 'ag_ranch'
  | 'lender_developer'
  | 'gov_reg'
  | 'municipality'
  | 'consultant';

export interface CARIConversation {
  id: string;
  site_id: string | null;
  title: string;
  capability_profile: CapabilityProfile;
  message_count: number;
  total_tokens: number;
  created_at: string;
  updated_at: string;
}

export interface CARIMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool_call' | 'tool_result';
  content: string | null;
  tool_name: string | null;
  tool_input: Record<string, unknown> | null;
  tool_output: Record<string, unknown> | null;
  ui_actions: unknown[] | null;
  tokens_used: number;
  latency_ms: number | null;
  rating: number | null;
  feedback_text: string | null;
  created_at: string;
}

export interface CARIConversationDetail extends CARIConversation {
  model_used: string;
  messages: CARIMessage[];
}

export interface Citation {
  index: number;
  tool_name: string;
  label: string;
}

// Inline renderable components from tool results (same shape as TERRA's).
export type InlineComponent =
  | { type: 'inline_table'; title: string; headers: string[]; rows: unknown[][] }
  | {
      type: 'inline_chart';
      chart_type: 'line' | 'bar' | 'area';
      title: string;
      series: { name: string; data: { x: string; y: number | null }[] }[];
      x_label?: string;
      y_label?: string;
    }
  | {
      type: 'inline_metrics';
      title: string;
      metrics: { label: string; value: string | number; unit?: string; status?: string }[];
    };

export interface ExtractedAttachment {
  filename: string;
  text: string;
  char_count: number;
  truncated: boolean;
}

export interface CARIStreamCallbacks {
  onStart?: (data: { conversation_id: string; message_id: string }) => void;
  onDelta?: (data: { text: string }) => void;
  onToolStart?: (data: { tool_name: string; tool_input: Record<string, unknown> }) => void;
  onToolEnd?: (data: { tool_name: string; summary: string; duration_ms: number }) => void;
  onUIComponent?: (data: InlineComponent) => void;
  onThinking?: (data: { text: string }) => void;
  onDone?: (data: { tokens_used: number; latency_ms: number; citations?: Citation[] | null }) => void;
  onError?: (data: { code: string; message: string; retry_after_seconds?: number }) => void;
}

// ── REST Endpoints ──────────────────────────────────────────────────────

export async function createConversation(
  siteId?: string,
  title?: string,
): Promise<CARIConversation> {
  const { data } = await apiClient.post(`${CARI_PREFIX}/conversations`, {
    site_id: siteId,
    title,
  });
  return data;
}

export async function listConversations(
  siteId?: string,
  limit = 20,
): Promise<CARIConversation[]> {
  const params: Record<string, string | number> = { limit };
  if (siteId) params.site_id = siteId;
  const { data } = await apiClient.get(`${CARI_PREFIX}/conversations`, { params });
  return data;
}

export async function loadConversation(
  conversationId: string,
): Promise<CARIConversationDetail> {
  const { data } = await apiClient.get(
    `${CARI_PREFIX}/conversations/${conversationId}`,
  );
  return data;
}

export async function renameConversation(
  conversationId: string,
  title: string,
): Promise<CARIConversation> {
  const { data } = await apiClient.patch(
    `${CARI_PREFIX}/conversations/${conversationId}`,
    { title },
  );
  return data;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await apiClient.delete(`${CARI_PREFIX}/conversations/${conversationId}`);
}

export async function submitFeedback(
  messageId: string,
  rating: 1 | -1,
  feedbackText?: string,
  reasonCode?: string,
): Promise<void> {
  await apiClient.patch(`${CARI_PREFIX}/messages/${messageId}/feedback`, {
    rating,
    feedback_text: feedbackText,
    reason_code: reasonCode,
  });
}

export async function extractAttachmentText(
  file: File,
): Promise<ExtractedAttachment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ExtractedAttachment>(
    `${CARI_PREFIX}/extract-text`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

// ── SSE Streaming ───────────────────────────────────────────────────────

/**
 * Stream a message to CARI via SSE. Returns an AbortController to cancel.
 *
 * Implementation lifted from the TERRA client (same event protocol) with
 * the URL pointed at the CARI endpoint. Premium UX features (delta
 * batching, thinking renderer, citation parser, tool-call cards) live in
 * the consuming components, not here — the client just decodes the
 * event stream and dispatches callbacks.
 */
export function streamMessage(
  conversationId: string,
  content: string,
  context: Record<string, unknown> | null,
  callbacks: CARIStreamCallbacks,
): AbortController {
  const controller = new AbortController();
  const url = `${API_BASE_URL}/api/v1${CARI_PREFIX}/conversations/${conversationId}/messages`;

  const doFetch = () =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify({ content, context }),
    });

  doFetch()
    .then(async (response) => {
      // 401 → try refresh cookie, then retry once.
      if (response.status === 401) {
        try {
          await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
          });
          response = await doFetch();
        } catch {
          // Refresh failed — fall through to the error handler below.
        }
      }
      if (!response.ok) {
        callbacks.onError?.({
          code: 'http_error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let finalized = false;

      const processLines = (lines: string[]) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'start':
                  callbacks.onStart?.(data);
                  break;
                case 'delta':
                  callbacks.onDelta?.(data);
                  break;
                case 'tool_start':
                  callbacks.onToolStart?.(data);
                  break;
                case 'tool_end':
                  callbacks.onToolEnd?.(data);
                  break;
                case 'ui_component':
                  callbacks.onUIComponent?.(data as InlineComponent);
                  break;
                case 'thinking':
                  callbacks.onThinking?.(data);
                  break;
                case 'done':
                  finalized = true;
                  callbacks.onDone?.(data);
                  break;
                case 'error':
                  finalized = true;
                  callbacks.onError?.(data);
                  break;
              }
            } catch {
              // Malformed JSON — skip the line; the next one might be valid.
            }
            currentEvent = '';
          }
        }
      };

      // Read chunks, split on newlines, leave any incomplete line in the
      // buffer until the next read appends to it.
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        processLines(lines);
      }
      if (buffer.trim()) {
        processLines(buffer.split('\n'));
      }
      if (!finalized) {
        // Safety net — the server should always send done or error but
        // network mid-flight can drop without a clean close.
        callbacks.onDone?.({ tokens_used: 0, latency_ms: 0 });
      }
    })
    .catch((err) => {
      if (err?.name === 'AbortError') return;
      callbacks.onError?.({
        code: 'fetch_error',
        message: err?.message || String(err),
      });
    });

  return controller;
}
